// Accessible modal dialog on top of the native <dialog> element (focus trap, Escape and
// inert background come from the platform). Focus returns to the opener on close.
import { useEffect, useId, useRef, type ReactNode } from "react";
import { useT } from "../i18n/index.tsx";
import { IconButton } from "./Button.tsx";
import { XIcon } from "./icons.tsx";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  /** Prevent closing while a request is in flight. */
  busy?: boolean;
  /** "side" renders a right-hand detail drawer (same native modal semantics). */
  variant?: "center" | "side";
  testId?: string;
}

export function Dialog({ open, onClose, title, description, children, footer, wide = false, busy = false, variant = "center", testId }: DialogProps) {
  const t = useT();
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      opener.current = document.activeElement;
      el.showModal();
    } else if (!open && el.open) {
      el.close();
      if (opener.current instanceof HTMLElement) opener.current.focus();
    }
  }, [open]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onCancel = (e: Event) => {
      e.preventDefault();
      if (!busy) onClose();
    };
    el.addEventListener("cancel", onCancel);
    return () => el.removeEventListener("cancel", onCancel);
  }, [busy, onClose]);

  return (
    <dialog
      ref={ref}
      className={`dialog${wide ? " dialog--wide" : ""}${variant === "side" ? " dialog--side" : ""}`}
      data-testid={testId}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
    >
      {open ? (
        <div className="dialog-inner">
          <div className="dialog-header">
            <div className="stack" style={{ gap: 4, flex: 1, minWidth: 0 }}>
              <h2 id={titleId}>{title}</h2>
              {description ? (
                <p id={descId} className="muted" style={{ fontSize: "var(--text-sm)" }}>
                  {description}
                </p>
              ) : null}
            </div>
            <IconButton label={t("common.close")} onClick={onClose} disabled={busy}>
              <XIcon />
            </IconButton>
          </div>
          <div className="dialog-body">{children}</div>
          {footer ? <div className="dialog-footer">{footer}</div> : null}
        </div>
      ) : null}
    </dialog>
  );
}
